import { useNavigate } from 'react-router-dom';

import { type ServicesProvider, type SupportHours } from '@ekhodealer/ekho-common';
import { Box, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { paths } from '@config/paths';
import { SettingsSection } from '@config/paths/settingsSections';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DAY_LABELS: Record<string, string> = {
	monday: 'Monday',
	tuesday: 'Tuesday',
	wednesday: 'Wednesday',
	thursday: 'Thursday',
	friday: 'Friday',
	saturday: 'Saturday',
	sunday: 'Sunday',
};

/**
 * Format 24-hour time string (e.g. "09:00") to 12-hour display (e.g. "9:00 AM")
 */
function formatTime(time: string): string {
	const [hourStr, minuteStr] = time.split(':');
	const hour = parseInt(hourStr, 10);
	const minute = minuteStr ?? '00';
	if (isNaN(hour)) return time;
	const period = hour >= 12 ? 'PM' : 'AM';
	const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
	return `${displayHour}:${minute} ${period}`;
}

export function DealerHoursSection() {
	const navigate = useNavigate();
	const spId = useCurrentServicesProviderId();
	const { data: servicesProvider } = useCurrentServicesProvider();

	// Read from support.schedule (same field edited in Business Details > Business Hours)
	const support = (
		servicesProvider as ServicesProvider & {
			support?: { schedule?: Record<string, SupportHours> };
		}
	)?.support;
	const schedule = support?.schedule;

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Stack itemsSpacing='16'>
				<Text size='bodySmall' color='secondary'>
					Business hours are read from your account settings.{' '}
					<button
						type='button'
						onClick={() =>
							navigate(
								paths.settingsSection.getHref(
									spId,
									SettingsSection.AccountBusinessDetails,
								),
							)
						}
						style={{
							background: 'none',
							border: 'none',
							padding: 0,
							color: 'var(--Primary-60, #0066CC)',
							cursor: 'pointer',
							textDecoration: 'underline',
							font: 'inherit',
						}}>
						Edit in Business Details
					</button>
				</Text>
				{schedule && Object.keys(schedule).length > 0 ? (
					<Stack itemsSpacing='8'>
						{DAY_KEYS.map((key) => {
							const hours = schedule[key];
							return (
								<div
									key={key}
									style={{
										display: 'flex',
										justifyContent: 'space-between',
									}}>
									<Text size='body' weight='semibold'>
										{DAY_LABELS[key]}
									</Text>
									<Text size='body' color='secondary'>
										{hours
											? `${formatTime(hours.open)} – ${formatTime(hours.close)}`
											: 'Closed'}
									</Text>
								</div>
							);
						})}
					</Stack>
				) : (
					<Text size='body' color='secondary'>
						No business hours configured. Set them in Business Details.
					</Text>
				)}
			</Stack>
		</Box>
	);
}
